import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  Rating,
  Avatar,
  Divider,
  Skeleton
} from '@mui/material';
import {
  Star,
  FitnessCenter as FitnessCenterIcon,
  Layers as LayersIcon,
  CalendarToday as CalendarIcon,
  Grade as GradeIcon,
  ChevronRight,
  ChatBubbleOutline,
  FlagOutlined
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import BodyMap from 'components/play/BodyMap';
import PlayVolumeRadar from 'components/play/PlayVolumeRadar';
import PlayTopMusclesCard from 'components/play/rank/PlayTopMusclesCard';
import StatCard from 'components/gamification/StatCard';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import ImageViewModal from 'components/shared/modals/ImageViewModal';
import { MY_PLAY_RESULTS, MY_MUSCLE_DISTRIBUTION, MY_WEEKLY_ROUTINE } from 'api/graphql/play';
import { metricLabel, muscleLabel, volumeByRegion } from 'components/play/constants';
import { countSeries, distinctSeries, trendOf } from 'components/play/weeklyTrend';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};
const weekday = (iso) => {
  try {
    return cap(format(parseISO(iso), 'EEEE', { locale: es }));
  } catch {
    return '';
  }
};

/**
 * OTRIX Play — "Mi Rango": dashboard personal de rendimiento (C11 + C6/C9). KPIs, radar de
 * volumen por región muscular (registrado vs. planificado en la rutina), mapa muscular, y la
 * tabla de resultados (estilo sesiones 1:1) con panel lateral de detalle. Es progreso PERSONAL:
 * Play no compite en un ranking.
 */
function PlayMyRankPage() {
  const { t } = useTranslation(['play', 'routes']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const title = t('muscleRank.myTitle', { ns: 'play', defaultValue: 'Mi rango' });
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(title);
  }, [title, setAppBarButton, setAppBarTitle]);

  const { data: resData, loading } = useQuery(MY_PLAY_RESULTS, { variables: {}, fetchPolicy: 'cache-and-network' });
  const { data: distData } = useQuery(MY_MUSCLE_DISTRIBUTION, { fetchPolicy: 'cache-and-network' });
  // La rutina alimenta la serie "Objetivo" del radar: lo que el usuario PLANIFICÓ entrenar.
  const { data: routineData } = useQuery(MY_WEEKLY_ROUTINE, { fetchPolicy: 'cache-and-network' });

  const results = useMemo(() => resData?.myPlayResults || [], [resData]);
  const distribution = useMemo(() => distData?.myMuscleDistribution || [], [distData]);
  const routineItems = useMemo(() => routineData?.myWeeklyRoutine?.items || [], [routineData]);

  const intensity = useMemo(() => {
    const max = Math.max(1, ...distribution.map((d) => d.percent));
    return distribution.reduce((acc, d) => {
      let level = 1;
      if (d.percent >= max * 0.66) level = 3;
      else if (d.percent >= max * 0.34) level = 2;
      acc[d.muscle_group] = level;
      return acc;
    }, {});
  }, [distribution]);

  /**
   * Las dos series del radar. Se normalizan a % del total (volumeByRegion) porque vienen en
   * unidades distintas: `count` son resultados registrados y `target_sets` son series planificadas.
   */
  const radarActual = useMemo(
    () => volumeByRegion(distribution.map((d) => ({ muscle: d.muscle_group, weight: d.count }))),
    [distribution]
  );

  const radarTarget = useMemo(
    () =>
      volumeByRegion(
        routineItems.map((i) => ({ muscle: i.exercise?.muscle_group, weight: i.target_sets || 1 }))
      ),
    [routineItems]
  );

  const totalResults = results.length;
  const exerciseTypes = useMemo(() => new Set(results.map((r) => r.exercise?.id).filter(Boolean)).size, [results]);
  const graded = useMemo(() => results.filter((r) => r.rating), [results]);
  const avgRating = graded.length ? (graded.reduce((s, r) => s + r.rating, 0) / graded.length).toFixed(1) : '—';
  const lastActivity = useMemo(() => {
    const dates = results.map((r) => r.created_at).filter(Boolean);
    if (!dates.length) return '—';
    return fmtDate(dates.sort((a, b) => new Date(b) - new Date(a))[0]);
  }, [results]);

  // Series de las últimas 8 semanas para los sparklines + variación semana vs. semana anterior.
  const trends = useMemo(() => {
    const getDate = (r) => r.created_at;
    return {
      resultsSeries: countSeries(results, getDate),
      typesSeries: distinctSeries(results, getDate, (r) => r.exercise?.id)
    };
  }, [results]);

  const vsLastWeek = t('perf.vsLastWeek', { ns: 'play', defaultValue: 'vs semana anterior' });

  const kpis = [
    {
      icon: FitnessCenterIcon,
      value: totalResults,
      label: t('perf.totalResults', { ns: 'play', defaultValue: 'Resultados registrados' }),
      sparkline: trends.resultsSeries,
      ...(trendOf(trends.resultsSeries) || {})
    },
    {
      icon: LayersIcon,
      value: exerciseTypes,
      label: t('perf.exerciseTypes', { ns: 'play', defaultValue: 'Tipos de ejercicio' }),
      sparkline: trends.typesSeries,
      ...(trendOf(trends.typesSeries) || {})
    },
    // Calificación media y Última actividad van sin badge: un "%" sobre una nota media o sobre
    // una fecha no significa nada.
    { icon: GradeIcon, value: avgRating, label: t('rank.avgRating', { ns: 'play', defaultValue: 'Calificación media' }) },
    { icon: CalendarIcon, value: lastActivity, label: t('perf.lastActivity', { ns: 'play', defaultValue: 'Última actividad' }), sparkline: trends.resultsSeries }
  ];

  // Skeleton con la forma real de la pantalla (KPIs + los dos paneles): un spinner centrado no
  // dice nada de lo que está por llegar y hace saltar el layout al resolver.
  if (loading && results.length === 0) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <SEOPageTitle title={title} />
        <Skeleton variant="text" width={220} height={48} sx={{ mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={132} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={title} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} letterSpacing="-0.02em" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('rank.subtitle', { ns: 'play', defaultValue: 'Tu progreso medido con tus resultados registrados.' })}
        </Typography>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={k.label}>
              <StatCard
                title={k.label}
                value={k.value}
                icon={<Icon sx={{ fontSize: 20 }} />}
                trend={k.trend}
                trendValue={k.trendValue}
                subtitle={k.trend ? vsLastWeek : undefined}
                sparkline={k.sparkline}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Radar de volumen + mapa muscular + top de músculos trabajados */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={4}>
          <PlayVolumeRadar
            title={t('rank.donutTitle', { ns: 'play', defaultValue: 'Volumen por grupo muscular' })}
            actual={radarActual}
            target={radarTarget}
            emptyLabel={t('rank.donutEmpty', { ns: 'play', defaultValue: 'Registra resultados para ver tu volumen por músculo.' })}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Box sx={{ p: 3, border: '1px solid', borderColor: 'hairline.main', borderRadius: 3, bgcolor: 'surface.content', height: '100%' }}>
            <BodyMap selected={null} onSelect={() => {}} distribution={intensity} showModeToggle />
          </Box>
        </Grid>
        <Grid item xs={12} lg={4}>
          {/* Solo LECTURA de myMuscleDistribution (alimenta también al generador de rutinas). */}
          <PlayTopMusclesCard distribution={distribution} />
        </Grid>
      </Grid>

      {/* Tabla de resultados (estilo sesiones 1:1) */}
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>
        {t('rank.tableTitle', { ns: 'play', defaultValue: 'Mis resultados' })}
      </Typography>

      {results.length === 0 ? (
        <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: 3 }}>
          <Star sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="body1" fontWeight={700}>{t('rank.tableEmpty', { ns: 'play', defaultValue: 'Aún no tienes resultados registrados.' })}</Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {results.map((r) => (
            <Box
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(r)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelected(r);
                }
              }}
              sx={{
                cursor: 'pointer',
                backgroundColor: 'surface.raised',
                border: '1px solid',
                borderColor: 'hairline.main',
                borderLeft: '4px solid',
                borderLeftColor: r.rating ? 'gold.main' : 'hairline.strong',
                borderRadius: '14px',
                p: { xs: 1.5, sm: 2 },
                transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s',
                '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' },
                '&:active': { transform: 'scale(0.98)' }
              }}>
              {/* A 320 la foto (48) + el valor + el chevron dejaban el nombre sin aire: la foto se
                  reduce y la fecha se corta con elipsis en vez de empujar. */}
              <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
                {r.photo_url && (
                  <Avatar
                    variant="rounded"
                    src={r.photo_url}
                    sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flex: 'none' }}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body1" fontWeight={700} noWrap>{r.exercise?.localized_name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {weekday(r.created_at)} · {fmtDate(r.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flex: 'none' }}>
                  <Typography variant="body1" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.value}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {metricLabel(t, r.metric)}
                  </Typography>
                </Box>
                <Stack spacing={0.5} alignItems="center" sx={{ flex: 'none', display: { xs: 'none', sm: 'flex' }, minWidth: 96 }}>
                  {r.rating ? (
                    <Rating value={r.rating} max={5} readOnly size="small" sx={{ color: 'gold.main' }} />
                  ) : (
                    <Chip size="small" variant="outlined" label={t('perf.notRated', { ns: 'play', defaultValue: 'Sin calificar' })} sx={{ color: 'text.secondary' }} />
                  )}
                  {r.feedback && (
                    <Chip size="small" icon={<ChatBubbleOutline sx={{ fontSize: 13 }} />} label={t('rank.hasComment', { ns: 'play', defaultValue: 'Comentario' })} sx={{ bgcolor: 'gold.soft', color: 'gold.main', height: 20 }} />
                  )}
                </Stack>
                <ChevronRight sx={{ color: 'text.secondary', flex: 'none' }} />
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* Panel lateral de detalle */}
      <SessionDetailDrawer open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>{selected.exercise?.localized_name}</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {selected.exercise?.muscle_group && (
                <Chip size="small" label={muscleLabel(t, selected.exercise.muscle_group)} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
              )}
              {selected.is_flagged && (
                <Chip size="small" icon={<FlagOutlined sx={{ fontSize: 14 }} />} label={t('ranking.inReview', { ns: 'play', defaultValue: 'En revisión' })} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
              )}
            </Stack>

            {selected.photo_url && (
              <Box
                component="img"
                src={selected.photo_url}
                alt=""
                onClick={() => setZoom(selected.photo_url)}
                sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', cursor: 'zoom-in', mb: 2 }}
              />
            )}

            <Stack spacing={1.25} divider={<Divider flexItem />}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{t('rank.detailMark', { ns: 'play', defaultValue: 'Marca' })}</Typography>
                <Typography variant="body2" fontWeight={700}>{selected.value} {metricLabel(t, selected.metric)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{t('rank.detailDate', { ns: 'play', defaultValue: 'Fecha' })}</Typography>
                <Typography variant="body2" fontWeight={700}>{weekday(selected.created_at)} · {fmtDate(selected.created_at)}</Typography>
              </Stack>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{t('rank.detailGrade', { ns: 'play', defaultValue: 'Calificación' })}</Typography>
                {selected.rating ? (
                  <>
                    <Rating value={selected.rating} max={5} readOnly size="small" sx={{ color: 'gold.main' }} />
                    {selected.rater?.full_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('perf.ratedBy', { ns: 'play', name: selected.rater.full_name, defaultValue: `Calificó ${selected.rater.full_name}` })}
                      </Typography>
                    )}
                    {selected.feedback && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{selected.feedback}</Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t('perf.notRated', { ns: 'play', defaultValue: 'Sin calificar' })}</Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </SessionDetailDrawer>

      <ImageViewModal open={Boolean(zoom)} src={zoom} alt={t('result.evidenceAlt', { ns: 'play', defaultValue: 'Evidencia' })} onClose={() => setZoom(null)} />
    </Box>
  );
}

export default PlayMyRankPage;
